import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, AlertCircle } from "lucide-react";

const VSTAT = {
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  approved: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  rejected: "bg-red-400/10 text-red-300 border-red-400/20",
  inactive: "bg-slate-400/10 text-slate-300 border-slate-400/20",
};

const BSTAT = {
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  confirmed: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  active: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  completed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  cancelled: "bg-slate-400/10 text-slate-300 border-slate-400/20",
  rejected: "bg-red-400/10 text-red-300 border-red-400/20",
};

export default function VendorDashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", brand: "", category: "sedan", transmission: "Manual", fuel_type: "Petrol",
    seats: 4, price_per_day: 0, price_per_km: 0, location: "", image_url: "", description: "",
  });

  const load = useCallback(async () => {
    if (user?.status !== "approved") return;
    try {
      const [v, b] = await Promise.all([api.get("/vendor/vehicles"), api.get("/vendor/bookings")]);
      setVehicles(v.data); setBookings(b.data);
    } catch (err) {
      // Not approved yet, or transient error — log for visibility
      console.error("[vendor] failed to load:", err?.message || err);
    }
  }, [user?.status]);
  useEffect(() => { load(); }, [load]);

  const createVehicle = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        seats: Number(form.seats), price_per_day: Number(form.price_per_day),
        price_per_km: Number(form.price_per_km),
      };
      const { data } = await api.post("/vendor/vehicles", payload);
      toast.success("Vehicle submitted for review");
      setVehicles((arr) => [data, ...arr]);
      setOpen(false);
      setForm({ name: "", brand: "", category: "sedan", transmission: "Manual", fuel_type: "Petrol", seats: 4, price_per_day: 0, price_per_km: 0, location: "", image_url: "", description: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setBookingStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/vendor/bookings/${id}/status`, { status });
      setBookings((arr) => arr.map((b) => (b.id === id ? data : b)));
      toast.success(`Booking ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (v) => {
    try {
      const { data } = await api.patch(`/vendor/vehicles/${v.id}`, { is_active: !v.is_active });
      setVehicles((arr) => arr.map((x) => (x.id === v.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const earnings = bookings.filter((b) => b.status === "completed").reduce((s, b) => s + (b.total_amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Vendor · Dashboard</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-3">Hello, {user?.name}.</h1>
        <p className="text-slate-400 mb-10">{user?.business_name}</p>

        {user?.status !== "approved" && (
          <div className="bg-amber-400/5 border border-amber-400/20 rounded-2xl p-6 mb-10 flex items-start gap-4">
            <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-200 font-medium">Account under review</p>
              <p className="text-slate-400 text-sm mt-1">Your vendor account is awaiting admin approval. Once approved, you can list vehicles and receive bookings.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Vehicles" value={vehicles.length} />
          <Stat label="Active bookings" value={bookings.filter((b) => ["confirmed", "active"].includes(b.status)).length} />
          <Stat label="Pending requests" value={bookings.filter((b) => b.status === "pending").length} />
          <Stat label="Earnings" value={`₹${earnings.toFixed(0)}`} />
        </div>

        <Tabs defaultValue="vehicles">
          <TabsList className="bg-[#0A111A] border border-white/5">
            <TabsTrigger value="vehicles" data-testid="tab-vehicles" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Vehicles</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-sky-400 data-[state=active]:text-[#050B14]">Bookings</TabsTrigger>
          </TabsList>

          <TabsContent value="vehicles" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="vendor-add-vehicle-btn" disabled={user?.status !== "approved"} className="bg-sky-400 text-[#050B14] hover:bg-sky-300 font-medium">
                    <Plus className="w-4 h-4 mr-2" /> Add vehicle
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A111A] border-white/10 text-white max-w-lg">
                  <DialogHeader><DialogTitle>List a vehicle</DialogTitle></DialogHeader>
                  <form onSubmit={createVehicle} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <VField label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testid="vf-brand" required />
                      <VField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="vf-name" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                          <SelectTrigger data-testid="vf-category" className="bg-[#050B14] border-white/10 text-white mt-2"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0A111A] border-white/10 text-white">
                            {["sedan", "suv", "hatchback", "luxury", "bike"].map((c) => (
                              <SelectItem key={c} value={c} className="capitalize focus:bg-white/5">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <VField label="Seats" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} testid="vf-seats" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <VField label="Transmission" value={form.transmission} onChange={(v) => setForm({ ...form, transmission: v })} testid="vf-trans" />
                      <VField label="Fuel" value={form.fuel_type} onChange={(v) => setForm({ ...form, fuel_type: v })} testid="vf-fuel" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <VField label="Price / day (₹)" type="number" value={form.price_per_day} onChange={(v) => setForm({ ...form, price_per_day: v })} testid="vf-ppd" required />
                      <VField label="Price / km (₹)" type="number" value={form.price_per_km} onChange={(v) => setForm({ ...form, price_per_km: v })} testid="vf-ppk" />
                    </div>
                    <VField label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="vf-location" required />
                    <VField label="Image URL" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="vf-img" />
                    <Button type="submit" data-testid="vf-submit-btn" className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 mt-2">Submit for review</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {vehicles.length === 0 ? (
              <div className="bg-[#0A111A] border border-white/5 rounded-xl p-10 text-center text-slate-400">No vehicles listed.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vehicles.map((v) => (
                  <div key={v.id} className="bg-[#0A111A] border border-white/5 rounded-xl p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500">{v.brand}</p>
                        <h3 className="text-lg font-medium">{v.name}</h3>
                      </div>
                      <Badge className={`${VSTAT[v.status]} border capitalize`}>{v.status}</Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{v.location} · {v.seats} seats · ₹{v.price_per_day}/day</p>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(v)} data-testid={`toggle-${v.id}`}
                      className="border-white/20 bg-transparent text-slate-300 hover:bg-white/5">
                      {v.is_active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {bookings.length === 0 ? (
              <div className="bg-[#0A111A] border border-white/5 rounded-xl p-10 text-center text-slate-400">No bookings yet.</div>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <div key={b.id} className="bg-[#0A111A] border border-white/5 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium">{b.vehicle_name}</h3>
                        <Badge className={`${BSTAT[b.status]} border capitalize`}>{b.status}</Badge>
                      </div>
                      <p className="text-sm text-slate-400">{b.customer_name} · {b.customer_phone || "no phone"}</p>
                      <p className="text-xs text-slate-500 mt-1">{b.pickup_location} → {b.drop_location}</p>
                      <p className="text-xs text-slate-500">{new Date(b.pickup_date).toLocaleString()} — {new Date(b.return_date).toLocaleString()}</p>
                    </div>
                    <p className="text-xl font-light text-sky-300">₹{b.total_amount}</p>
                    <div className="flex gap-2">
                      {b.status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => setBookingStatus(b.id, "confirmed")} data-testid={`accept-${b.id}`} className="bg-sky-400 text-[#050B14] hover:bg-sky-300">Accept</Button>
                          <Button size="sm" variant="outline" onClick={() => setBookingStatus(b.id, "rejected")} data-testid={`reject-${b.id}`} className="border-white/20 bg-transparent text-slate-300">Reject</Button>
                        </>
                      )}
                      {b.status === "confirmed" && (
                        <Button size="sm" onClick={() => setBookingStatus(b.id, "active")} className="bg-sky-400 text-[#050B14] hover:bg-sky-300">Start Trip</Button>
                      )}
                      {b.status === "active" && (
                        <Button size="sm" onClick={() => setBookingStatus(b.id, "completed")} className="bg-emerald-400 text-[#050B14] hover:bg-emerald-300">Complete</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A111A] border border-white/5 rounded-xl p-5">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</p>
      <p className="text-3xl font-light tracking-tight">{value}</p>
    </div>
  );
}

function VField({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>
      <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400" />
    </div>
  );
}
